import NotesPage from './NotesPage';
import { generateComponentDocs, generateStoryDocs } from '../../utils/documentationUtils';

export default {
  title: 'Pages/NotesPage',
  component: NotesPage,
  parameters: {
    layout: 'fullscreen',
    ...generateComponentDocs(
      // What problem does it solve?
      `\nNotesPage serves as the primary interface for managing notes related to sites.
        \n\n####_***It allows users to:***_
        \n- Log and review notes for various sites.
        \n- Switch between \`table\` and \`gallery\` views for better data visualization.
        \n\n- Search and filter notes based on:
        \n>- **Site Name**
        \n>- **Keyword**
        \n>- **Status** (Open, Closed, Review).
        \n\n- Sorting:
        \n>- **Table view:** Sorting enabled through sortable column headers.
        \n>- **Sorting in Gallery View:** Dropdown for sorting.\n\n`,
      // When & how to use it?
      `\n\nThis page is used by outreach workers to create, view, and manage notes related to sites. Also, note priority determines site priority.
        \n\n###_**Key Features**_

        \n\n**Search Functionality**: 
        \n- Powered by the \`Search\` component with fields for site name, keyword and status.

        \n\n**Breadcrumb Navigation**: 
        \n- Utilizes the \`Breadcrumbs\` component for providing navigation context.

        \n\n**View Modes**: 
        \n- Toggle between \`table\` and \`gallery\` views using the \`handleModeChange\` function.

        \n\n**Sorting**: 
        \n- **Table view:** Sorting enabled through sortable column headers (Site Name, Title, Status, Priority, and Date).
        \n- **Gallery View:** Dropdown for sorting by \`Newest\` and \`Oldest\` notes.

        \n\n**Pagination**: 
        \n- Supports paginated navigation via the \`Pagination\` component for both views.

        \n\n**Empty State**: 
        \n- Uses \`EmptyState\` to handle scenarios where no notes are available.\n\n`,
      // Current Usage
      `\nOnly managers and planners can access the web application. 
        \n\n###_**Access**_
        \n- Accessible on Vamos web for **managers** and **planners**.
        \n\n###_**Roles**_
        \n\n- Manager:
        \nFull access: can create, view, edit, and delete notes for all sites that are within their organization.
        \n- Planner:
        \nPartial access: can create, view, and edit (but not delete) notes for all sites within their organization (including notes they did not create).
        \n- User:
        \nNo access: can not access to the Vamos web app and cannot access the Notes page or create/modify notes within the web app. (They are restricted to mobile app usage only.)
        \n\n>Note: The access rights described here may not have been fully implemented at the time this documentation was created. The description of roles reflects the intended behavior for access rights as outlined above.\n\n`,
      // Related Components
      `\n- **[SearchBar](http://localhost:6006/?path=/docs/components-searchbar-search--docs)**: Search and filter notes.
        \n- **[Breadcrumbs](http://localhost:6006/?path=/docs/components-breadcrumbs--docs)**: Provides navigational context.
        \n- **[NotesTable](http://localhost:6006/?path=/docs/components-notes-notestable--docs)**: Displays notes in table format.
        \n- **[NoteCards](http://localhost:6006/?path=/docs/components-notes-notecards--docs)**: Displays notes in card/gallery format.
        \n- **[Pagination](http://localhost:6006/?path=/docs/components-pagination-pagination--docs)**: Handles paginated navigation.
        \n- **[EmptyState](http://localhost:6006/?path=/docs/components-emptystate--docs)**: Displays when no notes are found.
      \n`,
    ),
  },
  tags: ['autodocs'],
  argTypes: {
    organizationId: {
      control: { type: 'number' },
      description: 'The organization ID used to fetch notes data.',
      table: {
        type: { summary: 'number' },
        required: true,
      },
    },
  },
};

const Template = () => null;

export const NotesPageDocsOnly = Template.bind({});
NotesPageDocsOnly.parameters = generateStoryDocs(
  // When is this variant used?
  `\n**NotesPage**
      \n- The \`organizationId\` must be provided for fetching notes.
    \n\n**Includes:**
      \n- Search functionality.
      \n- Breadcrumbs for navigation.
      \n- View-switching between table and gallery modes.
      \n- Pagination for navigating through records.\n\n`,
  // Where is it currently used on the product?
  'User can navigate to this page using `Dashboard menu` --> `Notes`',
);
